import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

import { useArrival, type Arrival } from './arrival';

/**
 * The three states every screen has and only some of them drew.
 *
 * The audit of 2026-08-22 counted the columns: loading was empty for the whole
 * product bar one label, «повторить» was empty outright, and emptiness was
 * drawn five different ways — one of them inside a refusal's frame, so a list
 * with nothing in it looked like a server that had failed.
 *
 * The rule between them is the one `App.refreshSession` already follows and
 * had no visual half: **the skeleton is for when there is nothing at all.**
 * Once a screen holds data it keeps it — a refused refresh draws nothing, and
 * a singer mid-check-in does not watch the rehearsal vanish because one poll
 * came back empty.
 */

/**
 * The shape of what is coming, and nothing else.
 *
 * No animation. A pulse is the one movement in the app that nobody's finger
 * asked for — it would go on by itself while the person sat still, which is
 * what «движение, которое отвечает» rules out and the reason the shimmer is not
 * an eleventh case. What replaces this *is* animated, and only on the way in:
 * see `Settling` below. The bars are the two lines a card in this app carries —
 * a date line and a title — at the sizes the frames draw, and their widths are
 * ragged because a column of identical bars reads as a table rather than as a
 * list on its way.
 */
const SKELETON_ROWS: [number, number][] = [
  [172, 140],
  [158, 82],
  [168, 126],
];

/**
 * How long a screen has to stand there with nothing on it before a skeleton is
 * worth drawing.
 *
 * Counted from the end of the screen's own arrival and not from the mount, so
 * it is 420ms after a tap on a card, 300 after a tab, and 160 on the first
 * screen of a session. A skeleton drawn in the frame the screen mounts in is
 * three or four frames long against a server that answers in sixty
 * milliseconds: it says nothing, it only blinks. What it means is not «loading»
 * but «this is taking longer than it should», and that is a thing that can only
 * be said once the screen has arrived and is standing empty.
 */
const SKELETON_AFTER_MS = 160;

/**
 * And how long it has to stay once it is there.
 *
 * Without this the delay above only moves the blink: an answer at 440ms leaves
 * a skeleton that lived twenty. Something shown as a state has to last long
 * enough to be read as one. Held by `Settling` rather than here, because what
 * takes the skeleton off is the screen deciding it has data, and this component
 * is gone by then — see the departing copy below.
 */
const SKELETON_LEAST_MS = 400;

/** The run that takes the copy off, over the data already standing under it. */
const SKELETON_OUT_MS = 140;

/** The drawing itself, which both the real skeleton and its departing copy use. */
function SkeletonBars() {
  return (
    <div className="skeleton" aria-hidden="true">
      {SKELETON_ROWS.map(([top, bottom], index) => (
        <div className="card" key={index}>
          <span className="skeleton-line" style={{ width: top }} />
          <span className="skeleton-line skeleton-line--title" style={{ width: bottom }} />
        </div>
      ))}
    </div>
  );
}

/**
 * When a skeleton mounted at `now` is allowed on screen.
 *
 * From the end of the arrival or from now, whichever is later: a screen that is
 * still riding in has not yet had its chance to be filled, so the wait cannot
 * start until it has stopped.
 */
export function skeletonDueAt(arrival: Arrival | null, now: number): number {
  return Math.max(arrival?.endsAt ?? 0, now) + SKELETON_AFTER_MS;
}

export function Skeleton() {
  const arrival = useArrival();
  /** Whether the wait above is over. Until it is, this draws nothing at all. */
  const [due, setDue] = useState(false);

  useEffect(() => {
    const now = performance.now();
    const timer = window.setTimeout(() => {
      // Recorded on the arrival and not here, because the reader of it is the
      // `Settling` that replaces this - by which time this component is gone.
      if (arrival !== null && arrival.skeletonAt === null) arrival.skeletonAt = performance.now();
      setDue(true);
    }, skeletonDueAt(arrival, now) - now);
    return () => window.clearTimeout(timer);
  }, [arrival]);

  return due ? <SkeletonBars /> : null;
}

/**
 * How the data gets onto the screen, and there are three answers.
 *
 * Decided once, at the mount that put this content there, and never revisited —
 * a decision taken twice is an opacity that jumps, which is the whole thing
 * these three exist to prevent.
 */
export type Entrance =
  /**
   * The screen it is on is still arriving. Then nothing happens here at all:
   * the run the finger asked for is already carrying this, and a second one
   * multiplied into it — 0.45 of a screen times 0 of its contents is nought,
   * and what was visible fell out of the frame and gathered itself again.
   */
  | { kind: 'with' }
  /**
   * A skeleton was standing here. The data goes on in the frame, whole, and the
   * skeleton is drawn again over the top of it and dissolved. `since` is when
   * it first appeared, so that one shown a moment ago is given its four hundred
   * milliseconds before it is taken away.
   */
  | { kind: 'over'; since: number }
  /** Nothing was here, and the screen is home. The only case that develops. */
  | { kind: 'fade' };

/**
 * The data, arriving where the skeleton was standing.
 *
 * **One arrival has one owner** — see `arrival.tsx`. What this component does is
 * ask, once, whose arrival this content is inside, and the answer is usually
 * «somebody else's»: a screen riding in carries its contents with it, and they
 * have no entrance of their own. The 180ms developing that was the whole of
 * this component on 2026-08-31 is now the third case of three, and the rarest:
 * data landing on a column that is empty, on a screen that has already stopped.
 *
 * The skeleton is seen off rather than dropped, and that is the correction of
 * 2026-09-01. It used to be taken away in the frame the data arrived and the
 * data then developed from nought — a filled column, 180ms of empty one, a
 * filled column again. Now the data is put on whole and the skeleton dissolves
 * on top of it: there is no gap, because everything is already underneath.
 *
 * It draws no box of its own. `display: contents` in the stylesheet takes the
 * wrapper out of the box tree entirely, so what is inside it goes on being the
 * flex children of the screen's own column, at the screen's own gap — the
 * animation lands on each of them instead. A wrapper that laid anything out
 * would change the spacing of ten screens to animate them.
 *
 * Wrap what arrives *with* the data and nothing that was on screen before it:
 * anything else inside this fades in when the screen itself is mounted, which
 * is the push's job and already done.
 *
 * And nothing that runs an animation of its own. The rule in the stylesheet is
 * `.settling--fade > *`, at the same weight as `.sheet` and later in the file,
 * so a sheet left inside one of these stops arriving from under the screen and
 * fades in over 180ms instead. The person's card is where that happened: its
 * four sheets sat inside the wrap until the wrap was closed above them.
 */
export function Settling({ children }: { children: ReactNode }) {
  const arrival = useArrival();
  const entrance = useRef<Entrance | null>(null);
  if (entrance.current === null) entrance.current = entranceFor(arrival);
  const entry = entrance.current;

  /** The copy of the skeleton lying over the data: holding still, or going. */
  const [ghost, setGhost] = useState<'held' | 'going' | null>(
    entry.kind === 'over' ? 'held' : null,
  );
  const copy = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (entry.kind !== 'over') return;
    // What is left of the four hundred milliseconds a skeleton owes the person
    // who saw it. Usually nothing - a server that answers inside 400ms of the
    // skeleton appearing has taken more than half a second in all.
    const held = Math.max(0, SKELETON_LEAST_MS - (performance.now() - entry.since));
    const going = window.setTimeout(() => setGhost('going'), held);
    const gone = window.setTimeout(() => setGhost(null), held + SKELETON_OUT_MS);
    return () => {
      window.clearTimeout(going);
      window.clearTimeout(gone);
    };
  }, [entry]);

  // The gap a box of no height would otherwise add to the column it stands in.
  //
  // A flex item still gets a gap after it however short it is, so the wrapper
  // holding the departing copy would push everything under it down by one step
  // of the screen's rhythm - a jerk, introduced by the change that exists to
  // remove one. The step is read rather than assumed: it is 14 on a screen, 12
  // on the panel's, 10 inside a card, and `display: contents` means the box it
  // belongs to is not this element's parent in the DOM.
  useLayoutEffect(() => {
    const element = copy.current;
    if (element === null) return;
    element.style.marginBottom = `-${columnGap(element)}`;
  }, [ghost]);

  return (
    <div className={entry.kind === 'fade' ? 'settling settling--fade' : 'settling'}>
      {ghost !== null && (
        <div className="settling-ghost" ref={copy}>
          <div className={ghost === 'going' ? 'settling-ghost-copy settling-ghost-copy--out' : 'settling-ghost-copy'}>
            <SkeletonBars />
          </div>
        </div>
      )}
      {children}
    </div>
  );
}

/**
 * Whose arrival this content is inside, asked once.
 *
 * The order is the rule: the screen's own run wins over everything, a skeleton
 * that stood is next, and developing from nought is what is left when neither
 * is true. Outside a pushed screen or a tab there is no arrival to belong to,
 * and the last case is the honest answer.
 */
export function entranceFor(arrival: Arrival | null, now = performance.now()): Entrance {
  if (arrival === null) return { kind: 'fade' };
  if (now < arrival.endsAt) return { kind: 'with' };
  if (arrival.skeletonAt === null) return { kind: 'fade' };
  // One departing copy per screen, drawn by the first of these to arrive: the
  // roster holds two of them, and two dissolving skeletons over one column
  // would be the thing this is here to stop, twice.
  if (arrival.seenOff) return { kind: 'with' };
  arrival.seenOff = true;
  return { kind: 'over', since: arrival.skeletonAt };
}

/** The row gap of the box this element is actually laid out in. */
function columnGap(element: HTMLElement): string {
  let box = element.parentElement;
  // Past every wrapper that draws nothing - `.settling` itself is one of them.
  while (box !== null && getComputedStyle(box).display === 'contents') box = box.parentElement;
  const gap = box === null ? '' : getComputedStyle(box).rowGap;
  return /^[\d.]/.test(gap) ? gap : '0px';
}

/**
 * Nothing here yet, which is not a failure and must not look like one.
 *
 * A card with no tone and no edge, and two sentences: what there is none of,
 * and what will put something in it. The second one is the whole point — «пока
 * пусто» alone is a shrug, and the person is left to work out whether they are
 * waiting for somebody or being asked to do something.
 *
 * There is normally no button, because the way to act is already on the screen.
 * The one exception passes it as a child: the schedule takes its «Запланировать»
 * capsule off an empty list on purpose, so an empty schedule with no button
 * would be a regent with no way to add a first gathering.
 */
export function Empty({ fact, note, children }: { fact: string; note: string; children?: ReactNode }) {
  return (
    <div className="card card--empty">
      <p className="refusal-fact">{fact}</p>
      <p className="refusal-note">{note}</p>
      {children}
    </div>
  );
}

/**
 * What did not arrive, and the one thing to do about it.
 *
 * The gender agrees with the subject here rather than in the string, so the
 * sentence is one template and not six — the same trap as «не был» beside a
 * woman's name, and the reason step 4 of this audit exists.
 */
type Subject =
  | 'schedule'
  | 'members'
  | 'attendance'
  | 'history'
  | 'program'
  | 'sermons'
  | 'songs'
  | 'messages';

const CAME = { m: 'пришёл', f: 'пришла', n: 'пришло' } as const;

const SUBJECT: Record<Subject, [string, keyof typeof CAME]> = {
  schedule: ['Расписание', 'n'],
  members: ['Состав', 'm'],
  attendance: ['Посещаемость', 'f'],
  history: ['История', 'f'],
  program: ['Программа', 'f'],
  sermons: ['График проповедей', 'm'],
  songs: ['Песни хора', 'm'],
  messages: ['Сообщения', 'n'],
};

function subjectLine(subject: Subject): string {
  const [noun, gender] = SUBJECT[subject];
  return `${noun} не ${CAME[gender]}`;
}

export function Failed({ subject, onRetry }: { subject: Subject; onRetry: () => void }) {
  return (
    <section className="refusal card--toned card--toned--outline" data-tone="danger">
      <p className="label label--tone">Не загрузилось</p>
      <p className="refusal-fact">{subjectLine(subject)}</p>
      {/* The reason, and then the thing worth knowing: nothing anybody typed is
          at stake here. A failure to *load* costs a screen; a failure to send
          would cost an answer, and that one is still drawn where the tap was. */}
      <p className="refusal-note">
        Похоже, нет связи. Ответы и отметки не потеряются — они уже на сервере.
      </p>
      <button className="btn btn--outline" onClick={onRetry}>
        Попробовать снова
      </button>
    </section>
  );
}
